// Assembles the exact POST /v1/custom-fields bodies from draft fields + the
// human-confirmed scope. The AI never emits FMX IDs — scoping IDs enter the
// payload here, from the scope screen's selections, and nowhere else.
//
// Key order is deterministic (identity keys, then behavior, then scoping) so
// the dry-run preview and the test fixtures can assert byte-exact bodies.
//
// Pure module: no React, no Supabase, no fetch.

import { normalizeFieldType } from './fmxCustomFieldsApi.js';
import { normalizeFieldset } from './fieldsets.js';

function scopeIdsOf(value) {
  if (value === null || value === undefined || value === '') return [];
  const arr = Array.isArray(value) ? value : [value];
  return arr
    .map(v => (v && typeof v === 'object' ? v.id : v))
    .map(v => (typeof v === 'string' && /^\d+$/.test(v) ? parseInt(v, 10) : v))
    .filter(v => v !== null && v !== undefined && v !== '');
}

// Scoping keys that take a single integer vs. an array of integers, per the
// POST /v1/custom-fields body schema (live OpenAPI spec, 2026-07-29).
const SCALAR_SCOPING_KEYS = [
  'workRequestModuleID', 'workTaskModuleID', 'workTaskInstructionSetID',
  'inventoryTypeID', 'utilityProviderID', 'utilityServiceID',
  'automatedRequestModuleID', 'automatedRequestTypeID',
];
const ARRAY_SCOPING_KEYS = [
  'requestTypeIDs', 'equipmentTypeIDs', 'resourceTypeIDs', 'userTypeIDs',
  'userTypeRestrictionIDs',
];

/**
 * Build one POST body. `fieldsetScope` is the scope screen's selections for
 * this draft's fieldset: { [scopingKey]: {id,name} | [{id,name}…] | id | [id…] }.
 * Behavior flags are included only when true; options/defaults only when
 * non-empty — FMX treats absent nullable keys as their defaults, and sparse
 * bodies keep the dry-run preview honest about what is actually sent.
 *
 * A field's own `scopeOverride` wins per key over the fieldset default: a
 * question asked under only three of a document's seven request-type sections
 * is created scoped to exactly those three (rule R2).
 *
 * NOTE: `visibilityFormula` is deliberately NOT in the body. FMX's API cannot
 * set it — the POST/PUT write model (Fmx.ApiCore CustomFieldEditModel.cs) has
 * no such property, only the read model does. Formulas are applied by hand
 * after the push; see visibilityFormula.js.
 */
export function buildPostBody(draft, fieldsetScope = {}) {
  const body = {
    name: (draft.name ?? '').trim(),
    fieldset: normalizeFieldset(draft.fieldset),
    fieldType: normalizeFieldType(draft.fieldType),
  };
  if (draft.isRequired === true) body.isRequired = true;
  if (draft.description) body.description = String(draft.description);

  if (Array.isArray(draft.options) && draft.options.length > 0) {
    body.options = draft.options.map(String);
  }
  if (Array.isArray(draft.defaults) && draft.defaults.length > 0) {
    body.defaults = draft.defaults.map(String);
  }
  for (const flag of ['allowOtherOption', 'allowMultipleSelections', 'allowMultipleLines', 'disallowNegativeValues']) {
    if (draft[flag] === true) body[flag] = true;
  }
  if (Number.isInteger(draft.sortPosition)) body.sortPosition = draft.sortPosition;

  const effectiveScope = { ...fieldsetScope, ...(draft.scopeOverride || {}) };
  for (const key of SCALAR_SCOPING_KEYS) {
    const ids = scopeIdsOf(effectiveScope[key]);
    if (ids.length > 0) body[key] = ids[0];
  }
  for (const key of ARRAY_SCOPING_KEYS) {
    const ids = scopeIdsOf(effectiveScope[key]);
    if (ids.length > 0) body[key] = ids;
  }
  return body;
}

/**
 * Build all POST bodies in creation order: fieldset by fieldset (in
 * `fieldsetOrder`, i.e. the confirmed-scope order), by sortPosition within
 * each, name as the tiebreaker. Returns [{ draftId, body }].
 */
export function buildAllPostBodies(drafts, scope = {}, fieldsetOrder = []) {
  const orderOf = (fs) => {
    const i = fieldsetOrder.indexOf(fs);
    return i === -1 ? fieldsetOrder.length : i;
  };
  const sorted = [...(drafts || [])].sort((a, b) => {
    const fa = normalizeFieldset(a.fieldset) || '';
    const fb = normalizeFieldset(b.fieldset) || '';
    if (fa !== fb) return (orderOf(fa) - orderOf(fb)) || fa.localeCompare(fb);
    const pa = Number.isInteger(a.sortPosition) ? a.sortPosition : Number.MAX_SAFE_INTEGER;
    const pb = Number.isInteger(b.sortPosition) ? b.sortPosition : Number.MAX_SAFE_INTEGER;
    if (pa !== pb) return pa - pb;
    return String(a.name || '').localeCompare(String(b.name || ''));
  });
  return sorted.map(d => ({
    draftId: d.id,
    body: buildPostBody(d, scope[normalizeFieldset(d.fieldset)] || {}),
  }));
}
